import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, Observable, take } from 'rxjs';
import { ApplicationStore, ApplicationStoreType } from '../store/application.store';
import { OrganizationService } from '../services/organization.service';
import { OrganizationUrlSyncService } from '../services/organization-url-sync.service';

@Injectable({ providedIn: 'root' })
export class OrganizationAccessResolver {
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private organizationService = inject(OrganizationService);
    private urlSync = inject(OrganizationUrlSyncService);

    private resolving = false;

    public ensureSelectedOrganization(): void {
        if (this.resolving || this.applicationStore.selectedOrganization()) {
            return;
        }
        this.resolving = true;

        this.organizationService
            .getOrganizations()
            .pipe(take(1))
            .subscribe((organizations) => {
                this.resolving = false;

                if (!organizations || organizations.length === 0) {
                    this.applicationStore.setSelectedOrganizationAccess(null);
                    return;
                }
                this.urlSync.resolveSelectionFromUrl(organizations);
            });
    }
}

export const whenOrganizationAccessResolved = <T>(checkAccess: () => T): T | Observable<T> => {
    const applicationStore: ApplicationStoreType = inject(ApplicationStore);
    const accessResolver = inject(OrganizationAccessResolver);

    if (applicationStore.organizationAccessResolved()) {
        return checkAccess();
    }

    accessResolver.ensureSelectedOrganization();

    return toObservable(applicationStore.organizationAccessResolved).pipe(
        filter((resolved) => resolved),
        take(1),
        map(() => checkAccess()),
    );
};
